import React from 'react';
import {Button, Icon, Text} from '@mc/ui-v4';
import {mcCheck, mcClose, mcErase} from '@mc/react-ui-icons';
import classNames from 'classnames';
import {CiselnikFilterNode} from './filterTreeTypes';
import {UseFilterTreeResult} from './useFilterTree';
import {FilterTreeItem} from './FilterTreeItem';
import './FilterTree.scss';

type Props = {
    /** Data stromu (zatím mock, později z API). */
    tree: CiselnikFilterNode[];
    /**
     * Stav stromu (zaškrtnutí/rozbalení). Drží ho SearchPanel, aby výběr filtru PŘEŽIL přepnutí
     * „Zpět na seznam" (tento panel se odmountuje) a filtr dál platil i při procházení seznamu.
     */
    filterTree: UseFilterTreeResult;
    /**
     * Mobilní zobrazení přes celou obrazovku: v hlavičce se vlevo zobrazí křížek (zrušit změny)
     * a vpravo tlačítko potvrzení (mcCheck). Když nejsou předány, panel je vložený (desktop / list).
     */
    onCancel?: () => void;
    onConfirm?: () => void;
};

/**
 * Režim „Nastavení filtru" v SearchPanelu – stromový výběr hodnot číselníku
 * (kaskádová tri-state). Zatím pracuje nad mock daty, BEZ API. Napojení na
 * skutečné filtrování vrstev (layerConditions) přijde s endpointem číselníku.
 */
export const FilterTreePanel: React.FC<Props> = ({tree, filterTree, onCancel, onConfirm}) => {
    const {selectedCount, clearAll} = filterTree;
    const fullscreen = Boolean(onCancel || onConfirm);

    // Server nemá pole, které by explicitně řeklo „tohle je Přístupnost, tohle Kategorie" (uzly stromu
    // jsou ploché sourozenci ze self-relace číselníku). Dokud takové pole nepřibude, rozlišujeme podle
    // struktury: kořenový uzel BEZ potomků = samostatná hodnota (v návrhu „Přístupnost"), kořenový uzel
    // S potomky = rozbalitelná kategorie („Kategorie"). Když by šlo o topic bez plochých kořenů, sekce
    // se jen nezobrazí – neriskujeme špatné popsání kategorie jako Přístupnost.
    const flatNodes = tree.filter(node => !node.children || node.children.length === 0);
    const categoryNodes = tree.filter(node => node.children && node.children.length > 0);

    return (
        <div className={classNames('filter-tree-panel', {'filter-tree-fullscreen': fullscreen})}>
            <div className={'filter-tree-header'}>
                {/* Mobil: křížek vlevo zruší provedené změny (obnoví stav před otevřením filtru). */}
                {onCancel && <Button title={'Zrušit změny'} style={'default'} icon={<Icon icon={mcClose} size={'lg'} />} onClick={onCancel} />}

                {/* Desktop: titulek v hlavičce. Na mobilu je na samostatné řádce pod hlavičkou (viz níže). */}
                {!fullscreen && (
                    <Text type={'tileValue'} className={'filter-tree-title'}>
                        Nastavení filtru
                    </Text>
                )}

                {/* Desktop: „Vynulovat filtr" jako ikona v hlavičce (jako před úpravami). Na mobilu je dole. */}
                {!fullscreen && (
                    <Button
                        style={'link'}
                        cssStyle={{position: 'absolute', right: '8px'}}
                        title={'Vynulovat filtr'}
                        disabled={selectedCount === 0}
                        icon={<Icon icon={mcErase} />}
                        onClick={clearAll}
                    />
                )}

                {/* Mobil: potvrzení nastaveného filtru. */}
                {onConfirm && <Button title={'Potvrdit filtr'} style={'primary'} icon={<Icon icon={mcCheck} />} onClick={onConfirm} />}
            </div>

            {/* Mobil: titulek na samostatné řádce pod hlavičkovými tlačítky. */}
            {fullscreen && (
                <Text type={'tileValue'} className={'filter-tree-title filter-tree-title--block'}>
                    Nastavení filtru
                </Text>
            )}

            <div className={'filter-tree-list'}>
                {flatNodes.length > 0 && (
                    <>
                        <Text type={'value'} className={'filter-tree-section-title'}>
                            Přístupnost
                        </Text>
                        {flatNodes.map(node => (
                            <FilterTreeItem key={node.id} node={node} depth={0} tree={filterTree} />
                        ))}
                    </>
                )}

                {categoryNodes.length > 0 && (
                    <>
                        <Text type={'value'} className={'filter-tree-section-title'}>
                            Kategorie
                        </Text>
                        {categoryNodes.map(node => (
                            <FilterTreeItem key={node.id} node={node} depth={0} tree={filterTree} />
                        ))}
                    </>
                )}
            </div>

            {/* Mobil: „Vynulovat filtr" dole (odškrtne vše = výchozí stav bez filtrování). Na desktopu je ikona v hlavičce. */}
            {fullscreen && (
                <Button style={'link'} disabled={selectedCount === 0} icon={<Icon icon={mcErase} />} onClick={clearAll}>
                    {' '}
                    Vynulovat filtr
                </Button>
            )}
        </div>
    );
};
